import { createContext, useContext, useEffect, useState } from "react";
import { getUnreadCount } from "../services/notificationService";

// ─────────────────────────────────────────────────────────────────────────────
//  NotificationContext
//
//  Fournit le compteur de notifications non lues à toute l'application.
//  Utilisé par : Topbar (point rouge sur la cloche) et Sidebar (badge chiffre).
//
//  Pourquoi un contexte ?
//  → Évite que Topbar et Sidebar fassent chacun leur propre appel API.
//  → Un seul endroit pour mettre à jour le compteur (ex: après markAllAsRead).
// ─────────────────────────────────────────────────────────────────────────────

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {

  const [unreadCount, setUnreadCount] = useState(0);

  // ── Charge le compteur au montage, puis rafraîchit toutes les 60s ──────
  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60_000); // polling toutes les 60s
    return () => clearInterval(interval);             // nettoyage au démontage
  }, []);

  async function fetchCount() {
    try {
      const res = await getUnreadCount();
      setUnreadCount(res.data ?? 0); // ApiResponse → .data contient le nombre
    } catch {
      // Silencieux : on ne bloque pas l'UI si l'appel échoue
    }
  }

  // ── decrementUnread : appelé depuis NotificationsPage après markAsRead ──
  // Evite de refaire un appel API juste pour décrémenter de 1
  function decrementUnread(by = 1) {
    setUnreadCount(prev => Math.max(0, prev - by));
  }

  // ── resetUnread : appelé depuis NotificationsPage après markAllAsRead ──
  function resetUnread() {
    setUnreadCount(0);
  }

  return (
    <NotificationContext.Provider value={{ unreadCount, decrementUnread, resetUnread }}>
      {children}
    </NotificationContext.Provider>
  );
}

// Hook utilitaire — usage : const { unreadCount } = useNotifications();
export function useNotifications() {
  return useContext(NotificationContext);
}
